/* eslint-disable max-lines */
import { AdminTenantDetails } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppliancesTableCard } from '@hakimo-ui/hakimo-admin/feature-appliance';
import { CameraTablesCard } from './cards/CameraTablesCard';
import { DeleteTenantCard } from './cards/DeleteTenantCard';
import { LocationsTableCard } from './cards/LocationsTableCard';
import { TenantInfoCard } from './cards/TenantInfoCard';
import { CreateLocations } from './CreateLocations';
import { EditTenant } from './EditTenant';
import { UpdateMonitoringModal } from './UpdateMonitoringModal';
import { getMonitoringStatus, JENKINS_JOBS_URL } from './util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  useCanDeleteTenant,
  useCanUpdateTenant,
  useCanViewAppliances,
} from '@hakimo-ui/hakimo/util';

interface Props {
  tenant: AdminTenantDetails;
}

export function TenantAdminDetailsView(props: Props) {
  const { tenant } = props;
  const navigate = useNavigate();
  const canUpdateTenant = useCanUpdateTenant();
  const canDeleteTenant = useCanDeleteTenant();
  const canViewAppliances = useCanViewAppliances();
  const [isEditing, setIsEditing] = useState(false);
  const [isCreatingLocations, setIsCreatingLocations] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);

  const onClickBack = () => {
    navigate(-1);
  };

  const handleClickEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
  };

  const handleClickCreateLocations = () => {
    setIsCreatingLocations(true);
  };

  const handleCancelCreateLocations = () => {
    setIsCreatingLocations(false);
  };

  const handleCreateLocationsSuccess = () => {
    setIsCreatingLocations(false);
  };

  if (isEditing) {
    return (
      <EditTenant
        tenant={tenant}
        onCancel={handleCancelEdit}
        onSuccess={handleEditSuccess}
      />
    );
  }

  if (isCreatingLocations) {
    return (
      <CreateLocations
        tenantId={tenant.id}
        onCancel={handleCancelCreateLocations}
        onSuccess={handleCreateLocationsSuccess}
      />
    );
  }

  const handleCloseModal = () => {
    setShowMonitoringModal(false);
  };

  const titleWithLink = (
    <span className="flex items-center gap-2">
      <span>{tenant.name}</span>
      <Button
        variant="link"
        onClick={() =>
          window.open(JENKINS_JOBS_URL, '_blank', 'noopener,noreferrer')
        }
      >
        Jenkins Jobs
      </Button>
    </span>
  );

  const monitoringStatus = getMonitoringStatus(tenant);

  const pageActions = useMemo(() => {
    const dotColor = monitoringStatus.enabled ? 'bg-green-500' : 'bg-red-500';
    const content = (
      <span className="flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${dotColor}`} />
        <span className="text-onlight-text-1 dark:text-ondark-text-1 text-sm">
          {monitoringStatus.label}
        </span>
        {canUpdateTenant && (
          <PencilSquareIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-4 w-4" />
        )}
      </span>
    );
    return [
      <div key="monitoring-status">
        {canUpdateTenant ? (
          <button
            type="button"
            onClick={() => setShowMonitoringModal(true)}
            title="Manage Hakimo monitoring"
            className="border-onlight-line-2 dark:border-ondark-line-2 hover:bg-onlight-bg-2 dark:hover:bg-ondark-bg-2 inline-flex items-center rounded-full border px-3 py-1"
          >
            {content}
          </button>
        ) : (
          <span className="border-onlight-line-2 dark:border-ondark-line-2 inline-flex items-center rounded-full border px-3 py-1">
            {content}
          </span>
        )}
      </div>,
    ];
  }, [monitoringStatus.enabled, monitoringStatus.label, canUpdateTenant]);

  return (
    <>
      <Page
        title={titleWithLink as any}
        onClickBack={onClickBack}
        pageActions={pageActions}
      >
        <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full justify-center py-6">
          <div className="w-full max-w-6xl space-y-6 px-4">
            <TenantInfoCard
              tenant={tenant}
              onEditClick={canUpdateTenant ? handleClickEdit : undefined}
            />
            <LocationsTableCard
              locations={tenant.locations}
              supportUserEmail={tenant.supportUserEmail}
              onClickCreate={handleClickCreateLocations}
            />
            {canViewAppliances && <AppliancesTableCard tenantId={tenant.id} />}
            <CameraTablesCard
              tenantId={tenant.id}
              sourceSystem={tenant.productType}
            />
            {tenant.isActive && canDeleteTenant && (
              <DeleteTenantCard tenantId={tenant.id} tenantName={tenant.name} />
            )}
          </div>
        </div>
      </Page>

      <UpdateMonitoringModal
        open={showMonitoringModal}
        onClose={handleCloseModal}
        tenant={tenant}
      />
    </>
  );
}

export default TenantAdminDetailsView;
